import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

export default function ReviewSubjectsCard() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/review-types").then((r) => setRows(r.data)).catch(() => toast.error("Could not load review subjects")), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a name for the review subject"); return; }
    setSaving(true);
    try { await api.post("/review-types", { name: name.trim() }); toast.success("Subject added"); setName(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const save = async (r) => {
    try { await api.put(`/review-types/${r.id}`, { name: (r.name || "").trim() }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    try { await api.delete(`/review-types/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const a = rows[idx], b = rows[j];
    try {
      await Promise.all([
        api.put(`/review-types/${a.id}`, { order: b.order ?? j }),
        api.put(`/review-types/${b.id}`, { order: a.order ?? idx }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRow = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  if (!rows) return <div className="text-slate-400 text-sm" data-testid="review-subjects-loading">Loading review subjects…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5 max-w-3xl" data-testid="review-subjects-card">
      <div>
        <h3 className="font-semibold text-[#0B1A2F]">Review subjects</h3>
        <p className="text-xs text-slate-400 mt-1">Subjects offered in the <strong>Type</strong> dropdown when adding a review on an employee's Performance tab, and used to group the <strong>Performance Reviews</strong> report. <strong>Built-in</strong> subjects (Appraisal, Probation review, PIP) carry special workflow and can't be renamed away or deleted. Drag order with the arrows.</p>
      </div>

      <div className="divide-y divide-slate-100">
        {rows.length === 0 && <p className="py-3 text-slate-400 text-sm">No review subjects yet.</p>}
        {rows.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2 py-2.5" data-testid={`subject-row-${r.key}`}>
            <div className="flex flex-col">
              <button className="text-slate-300 hover:text-[#2EA3FD] disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`subject-up-${r.key}`}><ArrowUp size={13} /></button>
              <button className="text-slate-300 hover:text-[#2EA3FD] disabled:opacity-30" disabled={i === rows.length - 1} onClick={() => move(i, 1)} data-testid={`subject-down-${r.key}`}><ArrowDown size={13} /></button>
            </div>
            <Input className="h-9 flex-1" value={r.name || ""} disabled={r.system} onChange={(e) => setRow(r.id, "name", e.target.value)} data-testid={`subject-name-${r.key}`} />
            {r.system ? <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0 w-16 text-center">built-in</span>
              : <span className="shrink-0 w-16" />}
            <Button size="sm" variant="outline" className="h-9 disabled:opacity-30" disabled={r.system} onClick={() => save(r)} data-testid={`subject-save-${r.key}`}><Save size={14} /></Button>
            <Button size="sm" variant="ghost" className="h-9 text-rose-600 disabled:opacity-30" disabled={r.system} onClick={() => del(r)} data-testid={`subject-del-${r.key}`}><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <h4 className="text-sm font-semibold text-[#0B1A2F] mb-3">Add a review subject</h4>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[12rem]"><Label className="text-xs text-slate-500 block mb-1">Name</Label><Input className="h-9" placeholder="e.g. 90-day check-in" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="subject-new-name" /></div>
          <Button onClick={add} disabled={saving} className="bg-[#2EA3FD] hover:bg-[#1B8EE6] h-9" data-testid="subject-add-btn"><Plus size={15} className="mr-1" />Add</Button>
        </div>
      </div>
    </div>
  );
}
